import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle, CheckCircle, FileSpreadsheet, Loader2, XCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useImportEmployees } from '@/features/employees/api/use-employees';
import type { EmployeeImportResponse } from '@/features/employees/types';
import { useAutoLoadUpload } from '@/hooks/use-auto-load-upload';
import i18n from '@/lib/i18n';

/** Convert an Excel file to CSV. Returns the original file if already CSV. */
async function ensureCsv(file: File): Promise<File> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'csv') return file;

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) throw new Error('Workbook has no sheets');
  const sheet = workbook.Sheets[firstSheetName];
  if (!sheet) throw new Error('Sheet not found in workbook');
  const csv = XLSX.utils.sheet_to_csv(sheet);
  const csvName = file.name.replace(/\.[^.]+$/, '.csv');
  return new File([csv], csvName, { type: 'text/csv' });
}

export const Route = createFileRoute('/_authenticated/employees/import')({
  component: ImportEmployeesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    uploadId: typeof search.uploadId === 'string' ? search.uploadId : undefined,
  }),
});

type WizardStep = 'upload' | 'processing' | 'results';

function ImportEmployeesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { uploadId } = Route.useSearch();
  const importMutation = useImportEmployees();
  const [step, setStep] = React.useState<WizardStep>('upload');
  const [result, setResult] = React.useState<EmployeeImportResponse | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const autoImportTriggered = React.useRef(false);

  const {
    file: autoLoadedFile,
    uploadDetail,
    isLoading: isAutoLoading,
    error: autoLoadError,
  } = useAutoLoadUpload(uploadId);

  React.useEffect(() => {
    if (autoLoadedFile && !autoImportTriggered.current && step === 'upload') {
      autoImportTriggered.current = true;
      setStep('processing');
      ensureCsv(autoLoadedFile)
        .then((csvFile) => {
          const formData = new FormData();
          formData.append('file', csvFile);
          return importMutation.mutateAsync(formData);
        })
        .then((res) => {
          setResult(res);
          setStep('results');
          toast.success(i18n.t('settings:toast.importCompleteCreatedUpdated', { created: String(res.created), updated: String(res.updated) }));
        })
        .catch(() => {
          toast.error(i18n.t('settings:toast.importFailed'));
          setStep('upload');
        });
    }
  }, [autoLoadedFile, step, importMutation]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStep('processing');
    try {
      const csvFile = await ensureCsv(file);
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await importMutation.mutateAsync(formData);
      setResult(res);
      setStep('results');
      toast.success(i18n.t('settings:toast.importCompleteCreatedUpdated2', { created: String(res.created), updated: String(res.updated) }));
    } catch {
      toast.error(i18n.t('settings:toast.importFailed'));
      setStep('upload');
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">{t('employees.importEmployeesTitle')}</h1>
        <p className="text-muted-foreground">
          {t('employees.importDescription')}
        </p>
      </div>

      {step === 'upload' && (
        <>
          {isAutoLoading ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="mt-4 font-medium">{t('employees.loadingFromClassification')}</p>
                {uploadDetail && (
                  <p className="mt-2 text-sm text-muted-foreground">{uploadDetail.fileName}</p>
                )}
              </CardContent>
            </Card>
          ) : autoLoadError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{autoLoadError}</AlertDescription>
            </Alert>
          ) : (
            <Card>
              <CardHeader><CardTitle>{t('employees.uploadFile')}</CardTitle></CardHeader>
              <CardContent>
                <div
                  className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="file-drop-zone"
                >
                  <FileSpreadsheet className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-4 text-sm font-medium">{t('employees.clickToUpload')}</p>
                  <p className="text-xs text-muted-foreground">{t('employees.csvExcelSupported')}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => void handleFileSelect(e)}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  <p className="font-medium">{t('employees.expectedColumns')}</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>{t('employees.nameRequired')}</li>
                    <li>{t('employees.emailRequired')}</li>
                    <li>{t('employees.departmentMatchedByName')}</li>
                    <li>{t('employees.facilityMatchedByName')}</li>
                    <li>{t('employees.employeeIdColumn')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {step === 'processing' && (
        <Card>
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="mt-4 font-medium">{t('employees.processingImport')}</p>
            <Progress value={50} className="mt-4 w-64" />
          </CardContent>
        </Card>
      )}

      {step === 'results' && result && (
        <Card>
          <CardHeader><CardTitle>{t('employees.importResults')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm">{t('employees.created', { count: result.created })}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                <span className="text-sm">{t('employees.updated', { count: result.updated })}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-5 w-5 text-yellow-600">-</span>
                <span className="text-sm">{t('employees.skipped', { count: result.skipped })}</span>
              </div>
              {result.errors > 0 && (
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm">{t('employees.errorsCount', { count: result.errors })}</span>
                </div>
              )}
            </div>

            {result.errorDetails.length > 0 && (
              <div className="rounded border p-3 text-sm">
                <p className="font-medium mb-2">{t('employees.errors')}</p>
                <ul className="space-y-1">
                  {result.errorDetails.slice(0, 10).map((e, i) => (
                    <li key={i} className="text-destructive">
                      Row {e.row}: {e.field} - {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => { setStep('upload'); setResult(null); }}
                data-testid="import-another"
              >
                {t('employees.importAnother')}
              </Button>
              <Button
                onClick={() => void navigate({ to: '/employees' })}
                data-testid="view-employees"
              >
                {t('employees.viewEmployees')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
